// BugsFeaturesReleases.jsx
// Bug and Enhancement Tracking System page (Qwizard-only)
import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "./firebase";
import Menu from "./Menu";
import AdminLogViewer from "./components/AdminLogViewer";
import ReleaseVersionViewer from "./components/ReleaseVersionViewer";
import { seedInitialLogs, shouldSeedLogs } from "./utils/seedAdminLogs";
import { seedInitialVersions, shouldSeedVersions } from "./utils/seedReleaseVersions";
import { trackConversationIssue, markIssueResolved } from "./utils/conversationLogger";
import { getAllVersions } from "./utils/releaseVersions";
import LogEntryForm from "./components/LogEntryForm";

export default function BugsFeaturesReleases() {
  const [bgUrl, setBgUrl] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [isQwizard, setIsQwizard] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);
  const [activeTab, setActiveTab] = useState("bugs"); // "bugs" or "releases"
  const [searchTerm, setSearchTerm] = useState("");
  const [logFormOpen, setLogFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => {
    getDownloadURL(ref(storage, "media/Brainbackground.png")).then(setBgUrl);
  }, []);

  // Check if user is Qwizard and seed initial logs if needed
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsQwizard(false);
        setCheckingRole(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        const isQwiz =
          userData.role === "Qwizard" ||
          user?.email === "rob@izeus.org" ||
          user?.email === "suelaw42@gmail.com";
        setIsQwizard(isQwiz);
        
        // Seed initial logs and versions once if Qwizard and they don't exist
        if (isQwiz) {
          const needsLogSeeding = await shouldSeedLogs();
          if (needsLogSeeding) {
            try {
              await seedInitialLogs();
              console.log("✅ Initial log entries seeded");
            } catch (err) {
              console.error("⚠️ Failed to seed initial logs:", err);
            }
          }

          const needsVersionSeeding = await shouldSeedVersions();
          if (needsVersionSeeding) {
            try {
              await seedInitialVersions();
              console.log("✅ Initial version entries seeded");
            } catch (err) {
              console.error("⚠️ Failed to seed initial versions:", err);
            }
          }

          // Retroactive logging: Create entry for compilation error that was just fixed
          try {
            const { getLogEntry } = await import("./utils/adminLogs");
            const existingBug = await getLogEntry("bug-002"); // Check if already logged
            
            if (!existingBug) {
              console.log("📝 Retroactively logging compilation error bug...");
              const bugEntry = await trackConversationIssue({
                type: "bug",
                title: "Import error: getAllVersions not found in conversationLogger",
                description: `Compilation error: "export 'getAllVersions' (imported as 'getAllVersions') was not found in './utils/conversationLogger'". This was caused by incorrect import statement in BugsFeaturesReleases.jsx - getAllVersions should be imported from releaseVersions.js, not conversationLogger.js.`,
                priority: "high",
                tags: ["import-error", "compilation", "build", "conversationLogger"],
                conversationContext: "User reported compilation error after latest code release. Fixed by correcting import statement.",
              });
              
              const bugId = typeof bugEntry === "string" ? bugEntry : bugEntry.id;
              console.log("✅ Created retroactive bug entry:", bugId);
              
              // Mark as resolved immediately (already fixed)
              const versions = await getAllVersions();
              const latestVersion = versions && versions.length > 0 ? versions[0] : null;
              
              if (latestVersion) {
                await markIssueResolved(bugId, latestVersion.id, "Fixed by correcting import statement - getAllVersions imported from releaseVersions.js instead of conversationLogger.js");
                console.log(`✅ Marked ${bugId} as resolved in ${latestVersion.id}`);
              } else {
                const { updateLogEntry, addLogNote } = await import("./utils/adminLogs");
                await updateLogEntry(bugId, { status: "resolved" });
                await addLogNote(bugId, "Fixed by correcting import statement - getAllVersions imported from releaseVersions.js instead of conversationLogger.js");
                console.log(`✅ Marked ${bugId} as resolved (no version linked)`);
              }
            } else {
              console.log("✅ Compilation error bug already logged:", existingBug.id);
            }
          } catch (err) {
            console.error("⚠️ Failed to create retroactive bug entry:", err);
          }
          
          // Auto-test: Create "Countdown timer" entry and mark as resolved
          try {
            const { getLogEntry } = await import("./utils/adminLogs");
            const existingCountdown = await getLogEntry("enh-002"); // Check if already logged
            
            if (!existingCountdown) {
              console.log("📝 Auto-creating 'Countdown timer' entry...");
              const entry = await trackConversationIssue({
                type: "enhancement",
                title: "Countdown timer for the start of the quiz",
                description: "Countdown timer for the start of the quiz",
                priority: "medium",
                tags: ["timer", "quiz-start", "ui"],
                conversationContext: "User requested to add to log and mark as resolved",
              });
              
              const entryId = typeof entry === "string" ? entry : entry.id;
              console.log("✅ Created entry:", entryId);
              
              const versions = await getAllVersions();
              const latestVersion = versions && versions.length > 0 ? versions[0] : null;
              
              if (latestVersion) {
                await markIssueResolved(entryId, latestVersion.id, "Countdown timer implemented");
                console.log(`✅ Marked ${entryId} as resolved in ${latestVersion.id}`);
              } else {
                const { updateLogEntry, addLogNote } = await import("./utils/adminLogs");
                await updateLogEntry(entryId, { status: "completed" });
                await addLogNote(entryId, "Marked as resolved - countdown timer implemented");
                console.log(`✅ Marked ${entryId} as completed (no version linked)`);
              }
            } else {
              console.log("✅ Countdown timer entry already logged:", existingCountdown.id);
            }
          } catch (err) {
            console.error("⚠️ Failed to auto-create test entry:", err);
          }

          // Auto-log: Enhancement request from conversation
          try {
            const { getAllLogs } = await import("./utils/adminLogs");
            const allLogs = await getAllLogs();
            const existingTicker = allLogs.find(
              (log) =>
                log.type === "enhancement" &&
                log.title.toLowerCase().includes("ticker") &&
                log.title.toLowerCase().includes("justification")
            );

            if (!existingTicker) {
              console.log("📝 Auto-logging enhancement: Ticker for answer justification...");
              await trackConversationIssue({
                type: "enhancement",
                title: "Add a ticker to the answers showing the answer justification",
                description: "Add a ticker to the answers showing the answer justification",
                priority: "medium",
                tags: ["ticker", "answer-justification", "ui", "display"],
                conversationContext: "User requested enhancement: Add ticker showing answer justification",
              });
              console.log("✅ Enhancement logged successfully");
            } else {
              console.log("✅ Ticker enhancement already logged:", existingTicker.id);
            }
          } catch (err) {
            console.error("⚠️ Failed to auto-log enhancement:", err);
          }
        }
      } catch (err) {
        console.error("Error checking Qwizard role:", err);
        setIsQwizard(false);
      } finally {
        setCheckingRole(false);
      }
    });
    return () => unsub();
  }, []);

  // Redirect if not Qwizard
  if (!checkingRole && !isQwizard) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: bgUrl
            ? `linear-gradient(rgba(24,24,40,0.77), rgba(44,16,50,0.82)), url(${bgUrl}) center center / cover no-repeat`
            : "#223b4c",
          fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
          color: "#fff",
        }}
      >
        <div style={{ textAlign: "center", padding: 40 }}>
          <h2 style={{ color: "#ff6b6b" }}>Access Denied</h2>
          <p>This page is only available to Qwizard administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: bgUrl
          ? `linear-gradient(rgba(24,24,40,0.77), rgba(44,16,50,0.82)), url(${bgUrl}) center center / cover no-repeat`
          : "#223b4c",
        fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
      }}
    >
      {/* Top Bar with Menu Button */}
      <div
        style={{
          width: "100%",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            aria-label="Open menu"
            title="Menu"
            onClick={() => setShowMenu(true)}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.16)",
              color: "#ffe066",
              fontWeight: 900,
              borderRadius: 12,
              padding: "10px 14px",
              cursor: "pointer",
            }}
          >
            ☰ Menu
          </button>

          <div
            style={{
              fontWeight: 900,
              color: "#f8faff",
              fontSize: "1.1em",
            }}
          >
            Bugs/Features/Releases
          </div>

          {activeTab === "bugs" && (
            <button
              onClick={() => {
                setEditingEntry(null);
                setLogFormOpen(true);
              }}
              style={{
                padding: "12px 28px",
                borderRadius: 8,
                border: "none",
                background: "#47e373",
                color: "#111",
                fontWeight: 900,
                cursor: "pointer",
                minWidth: 220,
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              Create New Entry
            </button>
          )}
        </div>

        {activeTab === "bugs" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginLeft: "auto",
            }}
          >
            <label
              htmlFor="admin-log-search-top"
              style={{
                color: "#ccd6f6",
                fontWeight: 600,
                fontSize: "0.95em",
              }}
            >
              Search logs
            </label>
            <input
              id="admin-log-search-top"
              type="search"
              placeholder="Search by keyword, tag, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: 260,
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(0,0,0,0.3)",
                color: "#fff",
                fontSize: "0.95em",
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Sidebar instance */}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      {/* Content card */}
      <div
        style={{
          background: "rgba(28,34,58,0.67)",
          padding: "44px 54px 38px 54px",
          borderRadius: "2.2em",
          boxShadow: "0 8px 36px 8px #111b, 0 1.5px 12px #2ae8",
          minWidth: 340,
          maxWidth: "95vw",
          width: "95vw",
          color: "#e7f1fd",
          marginTop: 38,
        }}
      >
        {checkingRole ? (
          <div style={{ textAlign: "center", padding: 40, color: "#fff" }}>
            Loading...
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 24,
                borderBottom: "2px solid rgba(255,255,255,0.1)",
              }}
            >
              <button
                onClick={() => setActiveTab("bugs")}
                style={{
                  padding: "12px 24px",
                  border: "none",
                  background: activeTab === "bugs" ? "#47e373" : "transparent",
                  color: activeTab === "bugs" ? "#111" : "#fff",
                  fontWeight: 900,
                  cursor: "pointer",
                  borderRadius: "8px 8px 0 0",
                  borderBottom: activeTab === "bugs" ? "2px solid #47e373" : "2px solid transparent",
                }}
              >
                Bugs & Enhancements
              </button>
              <button
                onClick={() => setActiveTab("releases")}
                style={{
                  padding: "12px 24px",
                  border: "none",
                  background: activeTab === "releases" ? "#47e373" : "transparent",
                  color: activeTab === "releases" ? "#111" : "#fff",
                  fontWeight: 900,
                  cursor: "pointer",
                  borderRadius: "8px 8px 0 0",
                  borderBottom: activeTab === "releases" ? "2px solid #47e373" : "2px solid transparent",
                }}
              >
                Releases & Versions
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "bugs" ? (
              <>
                <AdminLogViewer
                  isQwizard={isQwizard}
                  searchTerm={searchTerm}
                  onEditEntry={(entry) => {
                    setEditingEntry(entry);
                    setLogFormOpen(true);
                  }}
                />
                <LogEntryForm
                  open={logFormOpen}
                  existingEntry={editingEntry}
                  onClose={() => {
                    setLogFormOpen(false);
                    setEditingEntry(null);
                  }}
                  onSuccess={() => {
                    setLogFormOpen(false);
                    setEditingEntry(null);
                  }}
                />
              </>
            ) : (
              <ReleaseVersionViewer isQwizard={isQwizard} />
            )}

          </>
        )}
      </div>
    </div>
  );
}
